import { useEffect, useRef, useState, type ReactNode } from "react";
import { Maximize2, Minimize2, RefreshCw, VideoOff } from "lucide-react";
import { api } from "@/lib/api";

const MAX_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 1500;

// Espera a que termine la recolección de candidatos ICE antes de mandar la oferta: más simple
// que soportar trickle ICE, y de sobra para vídeo en la misma red/túnel (sin STUN/TURN remoto).
function waitForIceGatheringComplete(pc: RTCPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") return Promise.resolve();
  return new Promise((resolve) => {
    function check() {
      if (pc.iceGatheringState === "complete") {
        pc.removeEventListener("icegatheringstatechange", check);
        resolve();
      }
    }
    pc.addEventListener("icegatheringstatechange", check);
  });
}

// Reproductor de vídeo en directo por WebRTC (protocolo WHEP contra MediaMTX, vía el backend
// como intermediario de la señalización). Sustituye a LiveVideoPlayer (HLS) específicamente
// para ver un partido en curso: la diferencia de latencia (milisegundos frente a 2-3s) importa
// de verdad ahí. offerUrl/terminateUrl son configurables porque hay dos formas de autorizar la
// señalización: sesión de usuario normal (matches.ts) o el token propio de un dispositivo sin
// login, tipo tablet/pantalla (deviceView.ts) — la negociación WebRTC en sí es idéntica.
export function LiveMatchVideoPlayer({
  offerUrl,
  terminateUrl,
  className = "",
  overlay,
}: {
  offerUrl: string;
  terminateUrl: string;
  className?: string;
  overlay?: ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const sessionUrlRef = useRef<string | null>(null);
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastOfferUrlRef = useRef(offerUrl);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;

    // Solo reiniciamos el contador de reintentos cuando cambia la cámara de verdad, no cuando
    // el propio reintento automático dispara este efecto vía "attempt".
    if (lastOfferUrlRef.current !== offerUrl) {
      lastOfferUrlRef.current = offerUrl;
      retryCountRef.current = 0;
    }

    setError(false);

    function scheduleRetryOrFail() {
      if (cancelled) return;
      if (retryCountRef.current < MAX_RETRIES) {
        const attemptNumber = retryCountRef.current + 1;
        retryCountRef.current = attemptNumber;
        retryTimeoutRef.current = setTimeout(() => setAttempt((a) => a + 1), RETRY_BASE_DELAY_MS * attemptNumber);
      } else {
        setError(true);
      }
    }

    async function connect() {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.addTransceiver("video", { direction: "recvonly" });
      pc.ontrack = (event) => {
        if (video) video.srcObject = event.streams[0];
      };
      pc.oniceconnectionstatechange = () => {
        if (["failed", "disconnected", "closed"].includes(pc.iceConnectionState)) {
          scheduleRetryOrFail();
        }
      };

      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        await waitForIceGatheringComplete(pc);
        if (cancelled) return;

        const response = await api.post<string>(offerUrl, pc.localDescription!.sdp, {
          headers: { "Content-Type": "application/sdp" },
          responseType: "text",
        });
        if (cancelled) return;

        sessionUrlRef.current = (response.headers["x-whep-session"] as string) ?? null;
        await pc.setRemoteDescription({ type: "answer", sdp: response.data });
      } catch {
        scheduleRetryOrFail();
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      pcRef.current?.close();
      pcRef.current = null;
      if (sessionUrlRef.current) {
        api.delete(terminateUrl, { params: { session: sessionUrlRef.current } }).catch(() => {});
        sessionUrlRef.current = null;
      }
    };
  }, [offerUrl, terminateUrl, attempt]);

  useEffect(() => {
    function onFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    }
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  }

  function retryNow() {
    retryCountRef.current = 0;
    setError(false);
    setAttempt((a) => a + 1);
  }

  return (
    <div ref={containerRef} className={`group relative bg-black ${isFullscreen ? "flex items-center" : ""} ${className}`}>
      {error ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-ink-900 text-ink-300">
          <VideoOff className="h-6 w-6" />
          <span className="text-xs">Sin señal de la cámara</span>
          <button
            type="button"
            onClick={retryNow}
            className="mt-1 flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 text-xs text-white transition-colors hover:bg-white/20"
          >
            <RefreshCw className="h-3 w-3" />
            Reintentar
          </button>
        </div>
      ) : (
        <video ref={videoRef} autoPlay muted playsInline controls controlsList="nofullscreen" className="h-full w-full object-contain" />
      )}
      {!error && overlay}
      <button
        type="button"
        onClick={toggleFullscreen}
        title={isFullscreen ? "Salir de pantalla completa" : "Agrandar"}
        className="absolute right-2 bottom-2 rounded-lg bg-ink-950/70 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
      >
        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
    </div>
  );
}
